import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import { PageHero, Card, C, Loader, PrimaryButton, GhostButton } from "@/components/district/kit";
import { ChecklistFiles, GoalsCard, DiscussionCard, MeetingsCard } from "@/components/GovBrief";

const isRoster = (name) => /committee/i.test(name || "") || (/board/i.test(name || "") && !/meeting/i.test(name || ""));

export default function ClubPrep() {
  const { clubId: routeClubId } = useParams();
  const [clubId, setClubId] = useState(null);
  const [data, setData] = useState(null);
  const load = async (cid) => { const r = await api.get(`/clubs/${cid}/prep`); setData(r.data); };
  useEffect(() => {
    // A specific club (owner/official drilling in from the district dashboard) takes priority;
    // otherwise fall back to the signed-in officer's own club.
    if (routeClubId) {
      setClubId(routeClubId);
      load(routeClubId).catch((e) => setData({ none: true, denied: e.response?.status === 403 }));
      return;
    }
    api.get("/me/home").then(async (r) => {
      if (r.data.club_id) { setClubId(r.data.club_id); await load(r.data.club_id); }
      else setData({ none: true });
    });
  }, [routeClubId]);
  if (!data) return <Loader label="Loading your documents…" />;
  if (data.none) return <p className="text-[16px]" style={{ color: C.sub }}>{data.denied ? "You don’t have access to this club." : "Preparation is available for club officers."}</p>;

  const docItems = data.items.filter((it) => !isRoster(it.name));
  const done = docItems.filter((it) =>
    data.documents.some((d) => d.item_id === it.item_id) ||
    (data.info_answers?.[it.item_id]?.text || "").trim().length > 0).length;
  const pct = docItems.length ? Math.round((done / docItems.length) * 100) : 100;

  const submit = async () => {
    try {
      const r = await api.post(`/clubs/${clubId}/prep/submit`);
      toast.success(r.data.incomplete ? "Submitted (some items still outstanding)" : "Preparation submitted");
      await load(clubId);
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
  };
  const statusLine = `${done} of ${docItems.length} documents done`
    + (data.prep_status === "submitted" ? (data.submitted_incomplete ? " · Submitted (incomplete)" : " · Submitted") : "")
    + (data.completed ? " · Visit completed (read-only)" : "");

  return (
    <div className="mx-auto w-full max-w-[900px] space-y-6" data-testid="club-prep-page">
      <PageHero title="Documents" subtitle={statusLine} stats={[{ value: `${pct}%`, label: "Complete" }]} />

      <Card>
        <div className="mb-4 h-2 w-full overflow-hidden rounded-full" style={{ background: "#EEF1F6" }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: C.indigo }} data-testid="prep-progress" />
        </div>
        <ChecklistFiles clubId={clubId} data={data} reload={() => load(clubId)} itemFilter={(it) => !isRoster(it.name)} />
        {data.can_edit && data.prep_status !== "submitted" && (
          <PrimaryButton data-testid="submit-prep" className="mt-5 w-full" onClick={submit}>
            <PaperPlaneTilt size={16} weight="bold" /> Submit to district
          </PrimaryButton>
        )}
        {data.can_edit && data.prep_status === "submitted" && !data.completed && (
          <GhostButton data-testid="resubmit-prep" className="mt-5 w-full" onClick={submit}>
            <PaperPlaneTilt size={16} weight="bold" /> Resubmit after corrections
          </GhostButton>
        )}
      </Card>

      <MeetingsCard clubId={clubId} />
      <GoalsCard clubId={clubId} />
      <DiscussionCard clubId={clubId} />
    </div>
  );
}
